import React, { useEffect, useState } from "react";
import { Button, Form, Table, Popconfirm, Typography, Empty } from "antd";
import { TableHeader } from "components";
import { getRequest, postRequest, putRequest, deleteRequest } from "api";
import { SaveOutlined, RollbackOutlined, EditOutlined, DeleteOutlined } from "@ant-design/icons";
import { EditCell } from "./EditCell";
import { errorMsg, successMsg } from "utils/msg";
import moment from "moment";

const { Link } = Typography;

const Editable = ({
  title,
  columns,
  endpoint,
  endpoints,
  primaryKey,
  saveFunc,
  deleteFunc,
  values,
  noFilter = false,
  deleteBtn = true,
  editBtn = true,
  addBtn = true,
  actionCol = true,
  isPost = false,
  getFilters,
  filters,
  ...props
}) => {
  const [form] = Form.useForm();
  const [tableColumns, setColumns] = useState([]);
  const [editingKey, setEditingKey] = useState("");
  const [mode, setMode] = useState("");

  const isEditing = (record) => `${record[primaryKey]}` === `${editingKey}`;

  const [state, setState] = useState({
    data: [],
    total: 0,
    loading: false,
    filter: {
      Offset: 0,
      Limit: 10,
    },
  });

  const handleChange = (pagination, filters, sorter) => {
    const tmpFilter = {};
    Object.keys(filters).forEach((key) => {
      if (filters[key] !== null) tmpFilter[key] = filters[key][0];
    });

    setState({
      ...state,
      filter: {
        ...tmpFilter,
        Limit: pagination.pageSize,
        Offset: pagination.current === 0 ? 0 : (pagination.current - 1) * pagination.pageSize,
      },
    });
  };

  const fetchData = async () => {
    try {
      setState({
        ...state,
        loading: true,
      });

      if (isPost) {
        const res = await postRequest(endpoints || "", {
          ...getFilters,
          ...state.filter,
        });
        setState({
          ...state,
          data: res.data.Data ?? [],
          loading: false,
          total: +res.data?.RowCount ?? res.data?.Data?.length ?? 0,
        });
      } else {
        const res = await getRequest(endpoints || "", {
          ...getFilters,
          ...state.filter,
        });
        setState({
          ...state,
          data: res.data.Data ?? [],
          loading: false,
          total: +res.data?.RowCount ?? res.data?.Data?.length ?? 0,
        });
      }
    } catch (error) {
      errorMsg(error?.Msg);
      setState({
        ...state,
        loading: false,
      });
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line
  }, [state.filter]);

  useEffect(() => {
    let tmp = columns;
    const mergedColumns = tmp.map((col) => {
      let column = {
        dataIndex: col.key ?? Date.now(),
        editable: true,
        fieldType: "string",
        align: col.textAlign ?? "left",
        render: (val) => <div style={{ textAlign: col.textAlign ?? "left" }}>{val}</div>,
        ...col,
      };
      if (!column.editable) {
        return column;
      }
      return {
        ...column,
        onCell: (record) => ({
          ...column,
          record,
          key: col.dataIndex,
          title: col.title,
          value: record[col.dataIndex],
          editing: isEditing(record),
          style: { width: "100%" },
        }),
      };
    });

    setColumns(mergedColumns);
    // eslint-disable-next-line
  }, [editingKey]);

  const handleAdd = () => {
    let c = state.data.find((d) => d[primaryKey] === "-1");
    if (!c) {
      setMode("NEW");
      let newObj = {
        [primaryKey]: "-1",
      };

      let tmp = [...state.data];
      tmp.unshift(newObj);
      setState({
        ...state,
        data: tmp,
      });

      setEditingKey("-1");
    }
  };

  const handleSave = async (record) => {
    if (saveFunc) {
      await saveFunc(mode);
    } else {
      try {
        if (mode === "") return errorMsg("Мэдээлэл хадгалах төрөл тодорхойгүй байна!");
        if (mode === "NEW") {
          const params = endpoint.keys ? { ...endpoint.keys } : {};
          await postRequest(endpoint.url, {
            ...params,
            ...form.getFieldsValue(),
            ...values,
            ...filters,
          });
        } else {
          await putRequest(endpoint.url, {
            ...record,
            ...form.getFieldsValue(),
            ...values,
            ...filters,
          });
        }
        successMsg("Мэдээллийг амжилттай хадгаллаа.");
      } catch (error) {
        return errorMsg(error?.Msg);
      }
    }
    setMode("");
    setEditingKey("");
    form.resetFields();
    fetchData();
  };

  const handleEdit = (record) => {
    setMode("EDIT");
    let tmp = {};
    Object.keys(record).forEach((key) => (tmp[key] = moment()));
    form.setFieldsValue({ ...record });
    setEditingKey(`${record[primaryKey]}`);
  };

  const handleDelete = async (record) => {
    try {
      if (deleteFunc) {
        deleteFunc(record);
      } else {
        await deleteRequest(`${endpoint.url}/${record[primaryKey]}`);
      }
      successMsg("Мэдээллийг амжилттай устгалаа.");

      fetchData();
    } catch (error) {
      errorMsg(error?.Msg);
    }
  };

  const handleCancel = () => {
    setState({
      ...state,
      data: state.data.filter((e) => e[primaryKey] !== "-1"),
    });
    setMode("");
    setEditingKey("");
    form.resetFields();
  };

  return (
    <Form form={form} component={false}>
      <Table
        title={
          title || addBtn
            ? () => {
                return <TableHeader title={title} addFunc={addBtn && handleAdd} />;
              }
            : null
        }
        loading={state.loading}
        scroll={{ x: true }}
        bordered
        columns={
          actionCol
            ? [
                ...tableColumns,
                {
                  title: "Үйлдэл",
                  dataIndex: "operation",
                  align: "center",
                  width: "100px",
                  render: (_, record) => {
                    const editable = isEditing(record);
                    return editable ? (
                      <div
                        style={{
                          textAlign: "center",
                        }}
                      >
                        <Button type="link" onClick={() => handleSave(record)} icon={<SaveOutlined />} />
                        <Button type="link" onClick={() => handleCancel()} icon={<RollbackOutlined />} danger />
                      </div>
                    ) : (
                      <div
                        style={{
                          textAlign: "center",
                        }}
                      >
                        {editBtn && (
                          <Button
                            type="link"
                            disabled={editingKey !== ""}
                            onClick={() => handleEdit(record)}
                            icon={<EditOutlined />}
                          />
                        )}
                        {deleteBtn && (
                          <Popconfirm
                            title="Сонгосон мэдээллийг устгах уу"
                            onConfirm={() => handleDelete(record)}
                            okText="Тийм"
                            cancelText="Үгүй"
                          >
                            <Link type="danger" disabled={editingKey !== ""}>
                              <DeleteOutlined />
                            </Link>
                          </Popconfirm>
                        )}
                      </div>
                    );
                  },
                },
              ]
            : tableColumns
        }
        dataSource={state.data}
        rowKey={(record) => record[primaryKey]}
        pagination={{
          total: state.total,
          current: state.filter.Offset / state.filter.Limit + 1,
          pageSize: state.filter.Limit,
          showTotal: (total) => ` Нийт бичлэг: ${total}`,
        }}
        components={{
          body: {
            cell: EditCell,
          },
        }}
        size="small"
        onChange={handleChange}
        {...props}
      />
    </Form>
  );
};

export default Editable;
